/**
 * Arabic display names for teams, competitions and rounds. Proper nouns are
 * translated from a curated dictionary (machine translation mangles team
 * names), keyed by the exact English name the provider stores. Anything not in
 * the dictionary gracefully falls back to the original Latin name.
 */
import type { Locale } from './index';

/** National teams (the World Cup pool) + the well-known clubs we surface. */
const TEAM_NAME_AR: Record<string, string> = {
  // --- National teams ------------------------------------------------------
  Algeria: 'الجزائر',
  Argentina: 'الأرجنتين',
  Australia: 'أستراليا',
  Austria: 'النمسا',
  Belgium: 'بلجيكا',
  'Bosnia & Herzegovina': 'البوسنة والهرسك',
  Brazil: 'البرازيل',
  Cameroon: 'الكاميرون',
  Canada: 'كندا',
  'Cape Verde Islands': 'الرأس الأخضر',
  Chile: 'تشيلي',
  Colombia: 'كولومبيا',
  'Congo DR': 'الكونغو الديمقراطية',
  'Costa Rica': 'كوستاريكا',
  Croatia: 'كرواتيا',
  Curaçao: 'كوراساو',
  Czechia: 'التشيك',
  Denmark: 'الدنمارك',
  Ecuador: 'الإكوادور',
  Egypt: 'مصر',
  England: 'إنجلترا',
  France: 'فرنسا',
  Germany: 'ألمانيا',
  Ghana: 'غانا',
  Greece: 'اليونان',
  Haiti: 'هايتي',
  Honduras: 'هندوراس',
  Iran: 'إيران',
  Iraq: 'العراق',
  Italy: 'إيطاليا',
  'Ivory Coast': 'ساحل العاج',
  Jamaica: 'جامايكا',
  Japan: 'اليابان',
  Jordan: 'الأردن',
  Mexico: 'المكسيك',
  Morocco: 'المغرب',
  Netherlands: 'هولندا',
  'New Zealand': 'نيوزيلندا',
  Nigeria: 'نيجيريا',
  Norway: 'النرويج',
  Panama: 'بنما',
  Paraguay: 'باراغواي',
  Peru: 'بيرو',
  Poland: 'بولندا',
  Portugal: 'البرتغال',
  Qatar: 'قطر',
  'Saudi Arabia': 'السعودية',
  Scotland: 'اسكتلندا',
  Senegal: 'السنغال',
  Serbia: 'صربيا',
  Slovakia: 'سلوفاكيا',
  Slovenia: 'سلوفينيا',
  'South Africa': 'جنوب أفريقيا',
  'South Korea': 'كوريا الجنوبية',
  Spain: 'إسبانيا',
  Sweden: 'السويد',
  Switzerland: 'سويسرا',
  Tunisia: 'تونس',
  Türkiye: 'تركيا',
  Ukraine: 'أوكرانيا',
  Uruguay: 'الأوروغواي',
  USA: 'الولايات المتحدة',
  Uzbekistan: 'أوزبكستان',
  Wales: 'ويلز',

  // --- Clubs (the famous ones across the surfaced leagues) -----------------
  'Manchester City FC': 'مانشستر سيتي',
  'Manchester United FC': 'مانشستر يونايتد',
  'Liverpool FC': 'ليفربول',
  'Arsenal FC': 'أرسنال',
  'Chelsea FC': 'تشيلسي',
  'Tottenham Hotspur FC': 'توتنهام',
  'Newcastle United FC': 'نيوكاسل',
  'Aston Villa FC': 'أستون فيلا',
  'West Ham United FC': 'وست هام',
  'Everton FC': 'إيفرتون',
  'Brighton & Hove Albion FC': 'برايتون',
  'Nottingham Forest FC': 'نوتنغهام فورست',
  'Crystal Palace FC': 'كريستال بالاس',
  'Fulham FC': 'فولهام',
  'Brentford FC': 'برينتفورد',
  'Wolverhampton Wanderers FC': 'وولفرهامبتون',
  'AFC Bournemouth': 'بورنموث',
  'Leeds United FC': 'ليدز يونايتد',
  'Burnley FC': 'بيرنلي',
  'Sunderland AFC': 'سندرلاند',
  'Real Madrid CF': 'ريال مدريد',
  'FC Barcelona': 'برشلونة',
  'Club Atlético de Madrid': 'أتلتيكو مدريد',
  'Atletico Madrid': 'أتلتيكو مدريد',
  'Sevilla FC': 'إشبيلية',
  'Valencia CF': 'فالنسيا',
  'Villarreal CF': 'فياريال',
  'Real Sociedad de Fútbol': 'ريال سوسيداد',
  'Real Betis Balompié': 'ريال بيتيس',
  'Athletic Club': 'أتلتيك بيلباو',
  'Girona FC': 'جيرونا',
  'Juventus FC': 'يوفنتوس',
  'AC Milan': 'إيه سي ميلان',
  'FC Internazionale Milano': 'إنتر ميلان',
  'SSC Napoli': 'نابولي',
  'AS Roma': 'روما',
  'SS Lazio': 'لاتسيو',
  'ACF Fiorentina': 'فيورنتينا',
  'Atalanta BC': 'أتالانتا',
  'FC Bayern München': 'بايرن ميونخ',
  'Borussia Dortmund': 'بوروسيا دورتموند',
  'Bayer 04 Leverkusen': 'باير ليفركوزن',
  'RB Leipzig': 'لايبزيغ',
  'Paris Saint-Germain FC': 'باريس سان جيرمان',
  'Olympique de Marseille': 'مارسيليا',
  'Olympique Lyonnais': 'ليون',
  'AS Monaco FC': 'موناكو',
  'Real Madrid': 'ريال مدريد',
};

const COMPETITION_NAME_AR: Record<string, string> = {
  'World Cup 2026': 'كأس العالم 2026',
  'Premier League': 'الدوري الإنجليزي الممتاز',
  'Primera Division': 'الدوري الإسباني',
  'La Liga': 'الدوري الإسباني',
  'Serie A': 'الدوري الإيطالي',
  Bundesliga: 'الدوري الألماني',
  'Ligue 1': 'الدوري الفرنسي',
  'UEFA Champions League': 'دوري أبطال أوروبا',
};

const ROUND_NAME_AR: Record<string, string> = {
  Final: 'النهائي',
  'Semi-finals': 'نصف النهائي',
  'Semi-final': 'نصف النهائي',
  'Quarter-finals': 'ربع النهائي',
  'Quarter-final': 'ربع النهائي',
  'Round of 16': 'دور الـ 16',
  'Round of 32': 'دور الـ 32',
  '3rd Place Final': 'تحديد المركز الثالث',
  'Third Place': 'تحديد المركز الثالث',
};

interface NamedTeam {
  name?: string | null;
  short_name?: string | null;
}

/**
 * Team display name for the locale. In Arabic, the curated name when known;
 * otherwise the Latin fallback. `compact` prefers the short code in English
 * (e.g. "SWE"); Arabic always uses the full Arabic name since it is short
 * enough and the short codes are Latin.
 */
export function localizeTeamName(
  team: NamedTeam | null | undefined,
  locale: Locale,
  compact = false,
): string {
  if (!team) return '-';
  const fallback = (compact ? team.short_name : null) ?? team.name ?? team.short_name ?? '-';
  if (locale !== 'ar') return fallback;
  const arabic = team.name ? TEAM_NAME_AR[team.name] : undefined;
  return arabic ?? fallback;
}

export function localizeCompetition(name: string | null | undefined, locale: Locale): string {
  if (!name) return '';
  if (locale !== 'ar') return name;
  return COMPETITION_NAME_AR[name] ?? name;
}

export function localizeRound(round: string | null | undefined, locale: Locale): string {
  if (!round) return '';
  if (locale !== 'ar') return round;
  const groupStage = round.match(/group\s*stage\s*-?\s*(\d+)/i);
  if (groupStage) return `دور المجموعات - الجولة ${groupStage[1]}`;
  if (/^group\s+/i.test(round)) return round.replace(/^group\s+/i, 'المجموعة ');
  return ROUND_NAME_AR[round] ?? round;
}
